import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { deleteJobAdmin } from '../services/jobService';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminJobs = () => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    jobType: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    avgApplicationsPerJob: 0
  });
  const [deletingJob, setDeletingJob] = useState(null);

  useEffect(() => {
    // Check if company filter was passed from navigation
    if (location.state?.companyFilter) {
      setCompanyFilter(location.state.companyFilter);
      setCompanyName(location.state.companyName || '');
      console.log('Company filter applied:', location.state.companyFilter, location.state.companyName);
    }
    fetchJobs();
  }, [location.state]);

  useEffect(() => {
    filterJobs();
  }, [jobs, filters, companyFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const jobsData = [];

      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = { id: jobDoc.id, ...jobDoc.data() };

        // Fetch company details
        if (jobData.companyId) {
          try {
            const companyDoc = await getDoc(doc(db, 'companies', jobData.companyId));
            if (companyDoc.exists()) {
              jobData.companyDetails = companyDoc.data();
            }
          } catch (error) {
            console.error('Error fetching company details:', error);
          }
        }

        // Get application count
        try {
          const applicationsSnapshot = await getDocs(collection(db, 'applications'));
          const applicationCount = applicationsSnapshot.docs.filter(
            appDoc => appDoc.data().jobId === jobData.id
          ).length;
          jobData.applicationCount = applicationCount;
        } catch (error) {
          console.error('Error fetching applications:', error);
          jobData.applicationCount = 0;
        }

        jobsData.push(jobData);
      }

      setJobs(jobsData);

      // Calculate stats
      const totalJobs = jobsData.length;
      const activeJobs = jobsData.filter(job => job.status === 'active').length;
      const totalApplications = jobsData.reduce((sum, job) => sum + (job.applicationCount || 0), 0);
      const avgApplicationsPerJob = totalJobs > 0 ? (totalApplications / totalJobs).toFixed(1) : 0;

      setStats({
        totalJobs,
        activeJobs,
        totalApplications,
        avgApplicationsPerJob
      });

    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    // Filter by company if set
    if (companyFilter) {
      filtered = filtered.filter(job => job.userId === companyFilter || job.companyId === companyFilter);
      console.log('Filtered by company:', companyFilter, 'Results:', filtered.length);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(job => job.status === filters.status);
    }

    if (filters.jobType !== 'all') {
      filtered = filtered.filter(job => job.jobType === filters.jobType);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(job => {
        const locationText = typeof job.location === 'object'
          ? `${job.location.city || ''} ${job.location.state || ''} ${job.location.country || ''}`.toLowerCase()
          : (job.location || '').toLowerCase();

        return job.title?.toLowerCase().includes(searchTerm) ||
          job.companyDetails?.name?.toLowerCase().includes(searchTerm) ||
          locationText.includes(searchTerm);
      });
    }

    // Sort by creation date (latest first)
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA; // Latest first
    });

    setFilteredJobs(filtered);
  };

  const fetchAppliedUsers = async (jobId) => {
    setLoadingUsers(true);
    console.log('Fetching applied users for job:', jobId);
    try {
      const applicationsSnapshot = await getDocs(collection(db, 'applications'));
      console.log('Total applications in DB:', applicationsSnapshot.docs.length);

      const jobApplications = applicationsSnapshot.docs.filter(
        doc => doc.data().jobId === jobId
      );
      console.log('Applications for this job:', jobApplications.length);

      // Map applications to user data - using embedded data from application document
      const usersData = jobApplications.map(appDoc => {
        const appData = appDoc.data();
        console.log('Processing application:', appDoc.id, appData);

        // Use the embedded applicant data from the application document
        // This matches the structure from applicationService.js (lines 73-104)
        return {
          id: appDoc.id,
          applicationId: appDoc.id,
          userId: appData.applicantId || appData.userId,
          appliedAt: appData.appliedAt,
          status: appData.status || 'pending',
          coverLetter: appData.coverLetter || '',
          resume: appData.resumeURL || appData.resume || '',
          // Applicant details (embedded in application)
          name: appData.applicantName || 'Unknown',
          email: appData.applicantEmail || 'No email',
          phone: appData.applicantPhone || 'No phone',
          experience: appData.applicantProfile?.experience || appData.experience || 'Not specified',
          skills: appData.applicantProfile?.skills || appData.skills || [],
          location: appData.applicantLocation || appData.location || 'Not specified',
          profilePicture: appData.applicantProfile?.profilePicture || null,
          // Additional profile data
          professionalSummary: appData.applicantProfile?.professionalSummary || '',
          education: appData.applicantProfile?.education || [],
          expectedSalary: appData.applicantProfile?.expectedSalary || null,
          noticePeriod: appData.applicantProfile?.noticePeriod || ''
        };
      });

      // Sort by application date (latest first)
      usersData.sort((a, b) => {
        const dateA = a.appliedAt?.toDate ? a.appliedAt.toDate() : new Date(a.appliedAt);
        const dateB = b.appliedAt?.toDate ? b.appliedAt.toDate() : new Date(b.appliedAt);
        return dateB - dateA;
      });

      console.log('Final users data:', usersData);
      setAppliedUsers(usersData);
    } catch (error) {
      console.error('Error fetching applied users:', error);
      setAppliedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
    fetchAppliedUsers(job.id);
  };

  const exportAppliedUsers = () => {
    if (appliedUsers.length === 0) return;

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Experience', 'Skills', 'Location', 'Applied Date', 'Status', 'Cover Letter'],
      ...appliedUsers.map(user => [
        user.name,
        user.email,
        user.phone,
        user.experience,
        Array.isArray(user.skills) ? user.skills.join(', ') : user.skills,
        user.location,
        formatDate(user.appliedAt),
        user.status,
        user.coverLetter || 'No cover letter'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedJob?.title || 'job'}_applications.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const formatSalary = (salaryObj) => {
    if (!salaryObj) return 'Not specified';

    // Handle new salary object structure
    if (typeof salaryObj === 'object' && salaryObj.min && salaryObj.max) {
      const min = parseInt(salaryObj.min);
      const max = parseInt(salaryObj.max);
      const currency = salaryObj.currency === 'USD' ? '$' : '₹';
      const period = salaryObj.period || 'month';
      const negotiable = salaryObj.negotiable ? ' (Negotiable)' : '';

      return `${currency}${min.toLocaleString()} - ${currency}${max.toLocaleString()}/${period}${negotiable}`;
    }

    // Handle legacy format
    if (typeof salaryObj === 'number') return `₹${salaryObj.toLocaleString()}`;

    return 'Not specified';
  };

  const handleDeleteJob = async (jobId, jobTitle) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${jobTitle}"?\n\nThis will permanently delete:\n- Job posting from database\n- All job data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingJob(jobId);

    try {
      const result = await deleteJobAdmin(jobId);

      if (result.success) {
        // Remove job from local state
        setJobs(prev => prev.filter(j => j.id !== jobId));
        setFilteredJobs(prev => prev.filter(j => j.id !== jobId));

        // Close modal if this job was selected
        if (selectedJob?.id === jobId) {
          setShowJobModal(false);
          setSelectedJob(null);
        }

        // Update stats
        setStats(prev => ({
          ...prev,
          totalJobs: prev.totalJobs - 1
        }));

        alert('Job deleted successfully!');
      } else {
        alert(`Failed to delete job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('An error occurred while deleting the job. Please try again.');
    } finally {
      setDeletingJob(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="px-6 py-8 sm:px-8 lg:px-10 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 rounded-3xl p-8 sm:p-10 text-white shadow-xl relative overflow-hidden border border-slate-800">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:30px_30px]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3 bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">
              {companyFilter && companyName ? `Postings by ${companyName}` : 'Opportunity Desk'}
            </h1>
            <p className="text-slate-400 font-medium max-w-2xl text-sm sm:text-base leading-relaxed">
              {companyFilter && companyName
                ? `Auditing all active corporate requisitions initialized by ${companyName}`
                : 'Central repository for auditing active corporate requisitions, managing placements, and regulating live platform campaigns.'
              }
            </p>
          </div>
          {companyFilter && (
            <button
              onClick={() => {
                setCompanyFilter(null);
                setCompanyName('');
                window.history.replaceState({}, document.title);
              }}
              className="px-5 py-3 bg-slate-900 text-white rounded-2xl hover:bg-slate-850 transition-all duration-200 text-sm font-semibold flex items-center gap-2 border border-slate-800 shadow-lg hover:shadow-indigo-950/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Reset Company Filter
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        <div className="bg-gradient-to-tr from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl flex items-center gap-4 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="absolute -right-8 -top-8 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/20 transition-all duration-300 pointer-events-none" />
          <div className="w-12 h-12 bg-indigo-500/10 text-indigo-450 rounded-2xl flex items-center justify-center border border-indigo-500/20 shadow-inner">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Total Campaigns</p>
            <p className="text-3xl font-extrabold text-white mt-1 tracking-tight">{stats.totalJobs}</p>
          </div>
        </div>

        <div className="bg-gradient-to-tr from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl flex items-center gap-4 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="absolute -right-8 -top-8 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all duration-300 pointer-events-none" />
          <div className="w-12 h-12 bg-emerald-500/10 text-emerald-450 rounded-2xl flex items-center justify-center border border-emerald-500/20 shadow-inner">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Active Postings</p>
            <p className="text-3xl font-extrabold text-white mt-1 tracking-tight">{stats.activeJobs}</p>
          </div>
        </div>

        <div className="bg-gradient-to-tr from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl flex items-center gap-4 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="absolute -right-8 -top-8 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all duration-300 pointer-events-none" />
          <div className="w-12 h-12 bg-purple-500/10 text-purple-450 rounded-2xl flex items-center justify-center border border-purple-500/20 shadow-inner">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-purple-400 uppercase tracking-wider">Applications Filed</p>
            <p className="text-3xl font-extrabold text-white mt-1 tracking-tight">{stats.totalApplications}</p>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <div className="bg-white/75 backdrop-blur-md rounded-3xl border border-slate-200/80 p-6 shadow-md">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Search Registry</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search title, company, location..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-650 focus:border-indigo-650 bg-white/50 text-sm transition-all duration-200 shadow-inner"
              />
              <svg className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Campaign Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-650 focus:border-indigo-650 bg-white/50 text-sm transition-all duration-200 shadow-inner cursor-pointer"
            >
              <option value="all">All Listings</option>
              <option value="active">✓ Active listing</option>
              <option value="inactive">⌛ Under Review</option>
              <option value="closed">⚠ Concluded</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Requisition Type</label>
            <select
              value={filters.jobType}
              onChange={(e) => setFilters(prev => ({ ...prev, jobType: e.target.value }))}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-650 focus:border-indigo-650 bg-white/50 text-sm transition-all duration-200 shadow-inner cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="full-time">Full Time</option>
              <option value="part-time">Part Time</option>
              <option value="contract">Contractual</option>
              <option value="internship">Internship</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', jobType: 'all', search: '' })}
              className="w-full py-2.5 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 font-bold text-sm transition-all duration-200 shadow-md shadow-slate-900/10 hover:shadow-lg hover:shadow-slate-900/20"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            All Requisitions ({filteredJobs.length})
          </h2>
          <div className="text-sm text-slate-500 font-medium bg-slate-100 px-3.5 py-1.5 rounded-xl border border-slate-200/50">
            Sorted by latest first
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => handleJobClick(job)}
                className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 p-6 hover:shadow-2xl cursor-pointer transition-all duration-300 hover:border-indigo-500/30 transform hover:-translate-y-1 shadow-md relative overflow-hidden group flex flex-col justify-between"
              >
                <div>
                  {/* Job Header */}
                  <div className="flex flex-col gap-3 mb-4">
                    <div className="flex justify-between items-start gap-4">
                      <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 group-hover:text-indigo-650 transition-colors line-clamp-2">
                        {job.title}
                      </h3>
                      <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        job.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                          : job.status === 'closed'
                            ? 'bg-rose-500/10 text-rose-600 border-rose-500/20'
                            : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                      }`}>
                        {job.status === 'active' ? '✓ Active listing' : job.status === 'closed' ? '⚠ Concluded' : '⌛ Under Review'}
                      </span>
                    </div>

                    {/* Company and Date */}
                    <div className="flex flex-wrap items-center gap-3 text-slate-500 text-xs">
                      <div className="flex items-center gap-1.5 font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg">
                        <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>{job.companyDetails?.name || job.company || 'Unknown Company'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-400">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                        </svg>
                        <span>{formatDate(job.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Job Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100 min-w-0">
                      <svg className="w-4 h-4 text-indigo-550 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs text-slate-700 font-bold truncate">
                        {job.location?.city || job.location?.state || job.location?.country
                          ? `${job.location.city || ''}, ${job.location.state || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                          : typeof job.location === 'string' ? job.location : 'Not specified'
                        }
                      </span>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100 min-w-0">
                      <svg className="w-4 h-4 text-emerald-550 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      <span className="text-xs text-slate-700 font-bold truncate">{formatSalary(job.salary)}</span>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100 min-w-0">
                      <svg className="w-4 h-4 text-violet-550 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                      <span className="text-xs text-slate-700 font-bold capitalize truncate">{job.jobType?.replace('-', ' ') || 'Not specified'}</span>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100 min-w-0">
                      <svg className="w-4 h-4 text-amber-550 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-xs text-slate-700 font-bold capitalize truncate">
                        {job.experienceRequired ? `${job.experienceRequired} Yrs Required` : (job.experienceLevel || 'Not specified')}
                      </span>
                    </div>
                  </div>

                  {/* Job Tags */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-4">
                    {job.urgent && (
                      <span className="px-2.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        🔥 URGENT
                      </span>
                    )}
                    {job.featured && (
                      <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        ⭐ FEATURED
                      </span>
                    )}
                    {job.department && (
                      <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        {job.department}
                      </span>
                    )}
                    {job.location?.hybrid && (
                      <span className="px-2.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-100 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        🏢 HYBRID
                      </span>
                    )}
                    {job.location?.remote && (
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                        🏠 REMOTE
                      </span>
                    )}
                  </div>

                  {/* Job Description */}
                  <p className="text-slate-600 text-xs sm:text-sm mb-5 leading-relaxed break-words line-clamp-3">
                    {job.description?.substring(0, 160)}...
                  </p>
                </div>

                {/* Job Footer */}
                <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-100">
                  <div className="text-xs text-slate-500 font-bold bg-slate-100 px-2 py-1 rounded-lg">
                    Applications: <span className="text-indigo-650">{job.applicationCount || 0}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteJob(job.id, job.title);
                      }}
                      disabled={deletingJob === job.id}
                      className={`px-3 py-1.5 bg-white border border-rose-200 hover:border-rose-350 hover:bg-rose-50/50 text-rose-600 rounded-xl transition-all duration-200 flex items-center gap-1 text-xs font-semibold ${deletingJob === job.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Delete listing"
                    >
                      {deletingJob === job.id ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Delete</span>
                        </>
                      )}
                    </button>
                    <span className="text-xs font-bold text-indigo-655 hover:text-indigo-750 flex items-center gap-0.5 group-hover:translate-x-0.5 transition-all duration-200">
                      Review Requisition
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-16 text-center bg-white/50 border border-dashed border-slate-350/60 rounded-3xl backdrop-blur-sm">
            <div className="text-slate-400 text-6xl mb-4">💼</div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No requisitions matching filters</h3>
            <p className="text-slate-500 max-w-md mx-auto">Please refine your status, search queries, or campaign filters to view corresponding postings.</p>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {showJobModal && selectedJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-5">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 p-6 sm:p-8 border-b border-slate-800">
              <div className="absolute inset-0 bg-grid-white/[0.01] bg-[size:20px_20px]" />
              <div className="absolute -right-8 -top-8 w-48 h-48 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-6">
                  {/* Job Icon */}
                  <div className="w-16 h-16 bg-indigo-500/15 rounded-2xl flex items-center justify-center text-white border border-indigo-500/30 shadow-lg text-2xl">
                    💼
                  </div>

                  <div className="text-white">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold mb-2 tracking-tight">{selectedJob.title}</h2>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2.5">
                      <span className={`px-3.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm border ${
                        selectedJob.status === 'active'
                          ? 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30'
                          : selectedJob.status === 'closed'
                            ? 'bg-rose-500/20 text-rose-100 border-rose-400/30'
                            : 'bg-amber-500/20 text-amber-100 border-amber-400/30'
                      }`}>
                        {selectedJob.status === 'active' ? '✓ Active listing' : selectedJob.status === 'closed' ? '⚠ Concluded' : '⌛ Under Review'}
                      </span>
                      <span className="px-3.5 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs font-semibold border border-white/20">
                        {selectedJob.companyDetails?.name || selectedJob.company || 'Unknown Company'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-slate-350 text-xs sm:text-sm font-medium">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>
                          {selectedJob.location?.city || selectedJob.location?.state || selectedJob.location?.country
                            ? `${selectedJob.location.city || ''}, ${selectedJob.location.state || ''}, ${selectedJob.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : typeof selectedJob.location === 'string' ? selectedJob.location : 'Location not specified'
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        <span className="font-semibold text-emerald-400">{formatSalary(selectedJob.salary)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowJobModal(false)}
                  className="absolute top-4 right-4 sm:relative sm:top-auto sm:right-auto p-2 hover:bg-white/10 rounded-xl transition-all duration-200 text-slate-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-6 sm:p-8 bg-slate-50/50 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-start">
                {/* Main details column (2/3 width on desktop) */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Job Description */}
                  <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60">
                    <div className="flex items-center gap-3 mb-5 border-b border-slate-100 pb-3">
                      <div className="w-9 h-9 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center border border-indigo-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">Job Description</h3>
                    </div>
                    <div className="prose max-w-none text-slate-700 leading-relaxed text-sm sm:text-base space-y-4">
                      {typeof selectedJob.description === 'string'
                        ? selectedJob.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="text-slate-655">{paragraph}</p>
                        ))
                        : <p className="text-slate-655">{selectedJob.description || 'No description available'}</p>
                      }
                    </div>
                  </div>

                  {/* Requirements */}
                  {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60">
                      <div className="flex items-center gap-3 mb-5 border-b border-slate-100 pb-3">
                        <div className="w-9 h-9 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center border border-rose-100">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">Requirements</h3>
                      </div>
                      <div className="space-y-3">
                        {Array.isArray(selectedJob.requirements)
                          ? selectedJob.requirements.map((req, index) => (
                            <div key={index} className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm sm:text-base">
                              <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-slate-700 font-medium">{req}</span>
                            </div>
                          ))
                          : <div className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm sm:text-base">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-slate-700 font-medium">{selectedJob.requirements}</span>
                          </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Responsibilities */}
                  {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60">
                      <div className="flex items-center gap-3 mb-5 border-b border-slate-100 pb-3">
                        <div className="w-9 h-9 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center border border-indigo-100">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-5a2 2 0 00-2-2h-2m-1 4h2a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2m-1 4h.01M9 16h.01" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">Responsibilities</h3>
                      </div>
                      <div className="space-y-3">
                        {Array.isArray(selectedJob.responsibilities)
                          ? selectedJob.responsibilities.map((resp, index) => (
                            <div key={index} className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm sm:text-base">
                              <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-slate-700 font-medium">{resp}</span>
                            </div>
                          ))
                          : <div className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm sm:text-base">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-slate-700 font-medium">{selectedJob.responsibilities}</span>
                          </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  {(selectedJob.requiredSkills?.length > 0 || selectedJob.preferredSkills?.length > 0) && (
                    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60 grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {selectedJob.requiredSkills && selectedJob.requiredSkills.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                            <div className="w-7 h-7 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center border border-orange-100 text-xs">
                              🔥
                            </div>
                            <h4 className="text-base font-bold text-slate-900">Required Skills</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedJob.requiredSkills.map((skill, index) => (
                              <span key={index} className="px-3 py-1 bg-orange-500/10 text-orange-700 text-xs font-bold rounded-xl border border-orange-500/20">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedJob.preferredSkills && selectedJob.preferredSkills.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                            <div className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center border border-emerald-100 text-xs">
                              ⭐
                            </div>
                            <h4 className="text-base font-bold text-slate-900">Preferred Skills</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedJob.preferredSkills.map((skill, index) => (
                              <span key={index} className="px-3 py-1 bg-emerald-500/10 text-emerald-700 text-xs font-bold rounded-xl border border-emerald-500/20">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Application Instructions */}
                  {selectedJob.applicationInstructions && selectedJob.applicationInstructions.trim() && (
                    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center border border-purple-100">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">Application Instructions</h3>
                      </div>
                      <div className="p-4 bg-purple-500/5 border border-purple-500/10 rounded-2xl">
                        <p className="text-slate-700 text-sm font-medium leading-relaxed">{selectedJob.applicationInstructions}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar details column (1/3 width on desktop) */}
                <div className="space-y-6">
                  <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-200/60 space-y-4">
                    <div className="flex items-center gap-3 mb-3 border-b border-slate-100 pb-3">
                      <div className="w-9 h-9 bg-slate-50 text-slate-655 rounded-xl flex items-center justify-center border border-slate-200/60">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">Requisition Info</h3>
                    </div>

                    {/* Company */}
                    <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Company</p>
                      <p className="text-slate-900 font-bold text-sm sm:text-base">{selectedJob.companyDetails?.name || selectedJob.company || 'Unknown'}</p>
                    </div>

                    {/* Location */}
                    <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Location</p>
                      <p className="text-slate-900 font-bold text-sm sm:text-base">
                        {selectedJob.location?.city || selectedJob.location?.state || selectedJob.location?.country
                          ? `${selectedJob.location.city || ''}, ${selectedJob.location.state || ''}, ${selectedJob.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                          : typeof selectedJob.location === 'string' ? selectedJob.location : 'Not specified'
                        }
                      </p>
                    </div>

                    {/* Salary */}
                    <div className="p-4 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 rounded-2xl border border-emerald-500/20">
                      <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Salary Bracket</p>
                      <p className="text-base sm:text-lg font-extrabold text-emerald-700">{formatSalary(selectedJob.salary)}</p>
                    </div>

                    {/* Meta properties grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Job Type</p>
                        <p className="text-slate-950 font-bold text-xs sm:text-sm capitalize">{selectedJob.jobType?.replace('-', ' ') || 'Not specified'}</p>
                      </div>

                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Work Mode</p>
                        <p className="text-slate-950 font-bold text-xs sm:text-sm capitalize">{selectedJob.workMode || 'Not specified'}</p>
                      </div>

                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Experience</p>
                        <p className="text-slate-950 font-bold text-xs sm:text-sm capitalize">
                          {selectedJob.experienceRequired ? `${selectedJob.experienceRequired} Yrs` : 'Not specified'}
                        </p>
                      </div>

                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Target Gender</p>
                        <p className="text-slate-950 font-bold text-xs sm:text-sm capitalize">
                          {selectedJob.gender === 'male' ? 'Male' : selectedJob.gender === 'female' ? 'Female' : 'Any (M/F)'}
                        </p>
                      </div>
                    </div>

                    {/* Department & Deadlines */}
                    <div className="space-y-3 pt-2">
                      <div className="flex justify-between items-center text-xs border-b border-slate-100 pb-2">
                        <span className="text-slate-450 font-bold uppercase tracking-wider">Department</span>
                        <span className="text-slate-800 font-bold capitalize">{selectedJob.department || 'General'}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs border-b border-slate-100 pb-2">
                        <span className="text-slate-450 font-bold uppercase tracking-wider">Published</span>
                        <span className="text-slate-800 font-bold">{formatDate(selectedJob.createdAt)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-450 font-bold uppercase tracking-wider">Deadline</span>
                        <span className="text-rose-600 font-bold">{formatDate(selectedJob.applicationDeadline) || 'No limit'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Campaign Tags */}
                  <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-200/60">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">Requisition Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.urgent && (
                        <span className="px-3 py-1 bg-rose-500/10 text-rose-700 text-xs font-bold rounded-lg border border-rose-500/20">
                          🔥 URGENT
                        </span>
                      )}
                      {selectedJob.featured && (
                        <span className="px-3 py-1 bg-amber-500/10 text-amber-700 text-xs font-bold rounded-lg border border-amber-500/20">
                          ⭐ FEATURED
                        </span>
                      )}
                      {selectedJob.location?.hybrid && (
                        <span className="px-3 py-1 bg-sky-500/10 text-sky-700 text-xs font-bold rounded-lg border border-sky-500/20">
                          🏢 HYBRID
                        </span>
                      )}
                      {selectedJob.location?.remote && (
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-500/20">
                          🏠 REMOTE
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${
                        selectedJob.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                          : selectedJob.status === 'closed'
                            ? 'bg-rose-500/10 text-rose-750 border-rose-500/20'
                            : 'bg-amber-500/10 text-amber-705 border-amber-500/20'
                      }`}>
                        {selectedJob.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Applied Candidates Subsection */}
              <div className="mt-8 bg-white rounded-3xl p-6 sm:p-8 shadow-md border border-slate-200/60">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-50 text-indigo-650 rounded-xl flex items-center justify-center border border-indigo-100 shadow-inner">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">Applied Candidates</h3>
                      <p className="text-xs text-slate-400 font-medium">Auditing {appliedUsers.length} total applicant profile files</p>
                    </div>
                  </div>

                  {appliedUsers.length > 0 && (
                    <button
                      onClick={exportAppliedUsers}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all duration-200 text-sm font-bold shadow-md shadow-slate-950/10 hover:shadow-lg"
                    >
                      <svg className="w-4 h-4 text-emerald-450" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Applicants CSV
                    </button>
                  )}
                </div>

                {loadingUsers ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-indigo-600"></div>
                    <span className="mt-3 text-slate-500 font-bold text-xs uppercase tracking-wider">Decompressing identity records...</span>
                  </div>
                ) : appliedUsers.length > 0 ? (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
                    {appliedUsers.map((user) => (
                      <div key={user.id} className="bg-slate-50/70 border border-slate-200/50 rounded-2xl p-5 hover:border-slate-300 hover:bg-slate-50 transition-all duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          {/* User Avatar */}
                          <div className="w-12 h-12 bg-gradient-to-tr from-slate-900 via-indigo-950 to-slate-900 rounded-full flex items-center justify-center text-white font-bold text-lg self-center sm:self-start border border-slate-800 shadow-md">
                            {user.name.charAt(0).toUpperCase()}
                          </div>

                          {/* User Details */}
                          <div className="flex-1 w-full space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <h4 className="text-base sm:text-lg font-bold text-slate-900">{user.name}</h4>
                              <span className={`self-start sm:self-auto px-3 py-1 rounded-full text-[10px] font-bold border ${
                                user.status === 'accepted'
                                  ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                  : user.status === 'rejected'
                                    ? 'bg-rose-500/10 text-rose-600 border-rose-500/20'
                                    : user.status === 'interviewed'
                                      ? 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20'
                                      : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                              }`}>
                                {user.status.toUpperCase()}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm text-slate-500">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="break-all font-medium text-slate-700">{user.email}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span className="font-medium text-slate-700">{user.phone}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="font-medium text-slate-700">{user.location}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                                </svg>
                                <span className="font-medium text-slate-750">Filing Date: {formatDate(user.appliedAt)}</span>
                              </div>
                            </div>

                            {/* Experience */}
                            <div className="text-xs sm:text-sm border-t border-slate-200/60 pt-2.5">
                              <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px] block mb-1">Work History</span>
                              <span className="text-slate-800 font-semibold">
                                {Array.isArray(user.experience) && user.experience.length > 0
                                  ? user.experience.map((exp, idx) =>
                                    `${exp.jobTitle || 'N/A'} at ${exp.company || 'N/A'}`
                                  ).join(', ')
                                  : typeof user.experience === 'string'
                                    ? user.experience
                                    : 'Not specified'
                                }
                              </span>
                            </div>

                            {/* Skills */}
                            {user.skills && user.skills.length > 0 && (
                              <div className="space-y-1.5">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px] block">Verified Skills</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).slice(0, 6).map((skill, index) => (
                                    <span key={index} className="px-2.5 py-1 bg-indigo-500/10 text-indigo-700 text-xs font-semibold rounded-lg border border-indigo-500/20">
                                      {skill.trim()}
                                    </span>
                                  ))}
                                  {(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).length > 6 && (
                                    <span className="px-2 py-1 bg-slate-200/60 text-slate-600 text-xs font-bold rounded-lg">
                                      +{(Array.isArray(user.skills) ? user.skills : user.skills.split(',')).length - 6} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Cover Letter */}
                            {user.coverLetter && (
                              <div className="mt-3 p-3.5 bg-white rounded-2xl border border-slate-200/60 shadow-inner">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px] block mb-1">Applicant statement</span>
                                <p className="text-slate-655 text-xs sm:text-sm leading-relaxed line-clamp-3 italic">"{user.coverLetter}"</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <div className="text-slate-350 text-4xl mb-3">👥</div>
                    <h4 className="text-base font-bold text-slate-700 mb-1">No Placement Applications</h4>
                    <p className="text-slate-450 text-xs">Candidates have not submitted applications to this requisition campaign yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminJobs;
